import { Alert, Modal, Stack } from '@mantine/core'
import { IconExclamationMark } from '@tabler/icons-react'
import { useEffect } from 'react'

import { useCreditPurchase } from '@/hooks/useCreditPurchase'
import type { PaymentMethod, Plan } from '@/services/api/api.types'

import { CreditPurchasePlanSelection } from './CreditPurchasePlanSelection'
import { PaymentFlowContent } from './PaymentFlowContent'

interface CreditPurchaseModalProps {
  opened: boolean
  onClose: () => void
  plans: Plan[]
  onPurchaseComplete: () => Promise<void>
  paymentMethod?: PaymentMethod | null
  currentAutoRefillEnabled: boolean
  currentCredits: number
}

export function CreditPurchaseModal({
  opened,
  onClose,
  plans,
  onPurchaseComplete,
  paymentMethod = null,
  currentAutoRefillEnabled,
  currentCredits,
}: CreditPurchaseModalProps) {
  const {
    selectedPlan,
    selectedPlanData,
    showPaymentForm,
    clientSecret,
    isLoading,
    error,
    purchaseCreditsNoPaymentMethodMutation,
    handlePlanSelect,
    handlePurchase,
    handlePaymentSuccess,
    handleBackFromPayment,
    resetModalState,
  } = useCreditPurchase({
    plans,
    paymentMethod,
    currentAutoRefillEnabled,
    currentCredits,
    onPurchaseComplete,
    onClose,
  })

  // Reset modal state when opened
  useEffect(() => {
    if (opened) {
      resetModalState()
    }
  }, [opened, resetModalState])

  const handleCancel = () => {
    onClose()
  }

  // Determine modal configuration based on current state
  const getModalConfig = () => {
    if (showPaymentForm && selectedPlanData) {
      return {
        title: 'Complete Your Purchase',
        size: clientSecret ? ('calc(100vw - 3rem)' as const) : ('lg' as const),
        maxWidth: clientSecret ? '1200px' : undefined,
        styles: clientSecret
          ? {
              body: { padding: '2rem' },
              header: { padding: '1.5rem 2rem 0 2rem' },
            }
          : undefined,
      }
    }
    return {
      title: 'Purchase Credits',
      size: 'xxl' as const,
      maxWidth: undefined,
      styles: undefined,
    }
  }

  const modalConfig = getModalConfig()

  // Render modal content based on current state
  const renderModalContent = () => {
    // Use shared payment flow component
    if (showPaymentForm && selectedPlanData) {
      return (
        <PaymentFlowContent
          showPaymentForm={showPaymentForm}
          clientSecret={clientSecret}
          selectedPlanData={selectedPlanData}
          plans={plans}
          autoRefill={false}
          isLoading={isLoading}
          hasError={!!purchaseCreditsNoPaymentMethodMutation.error}
          onPaymentSuccess={handlePaymentSuccess}
          onBackFromPayment={handleBackFromPayment}
          onRetry={() => purchaseCreditsNoPaymentMethodMutation.mutate()}
          loadingText="Setting up your payment..."
          mainText="Enter your payment details to purchase credits"
        />
      )
    }

    // Default state - plan selection
    return (
      <Stack gap="md">
        {error && (
          <Alert variant="light" color="red" title="Error" icon={<IconExclamationMark />}>
            {error}
          </Alert>
        )}
        <CreditPurchasePlanSelection
          plans={plans}
          selectedPlan={selectedPlan}
          onPlanSelect={handlePlanSelect}
          onPurchase={handlePurchase}
          onCancel={handleCancel}
          isUpdating={isLoading}
          paymentMethod={paymentMethod}
        />
      </Stack>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={modalConfig.title}
      size={modalConfig.size}
      maw={modalConfig.maxWidth}
      centered
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      styles={modalConfig.styles}>
      {renderModalContent()}
    </Modal>
  )
}
